/**
 * The API client.
 *
 * One place that knows about credentials and CSRF, so no call site has to
 * remember. Every request sends cookies; every unsafe request sends the CSRF
 * header read from the readable companion cookie (double submit).
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** A 401 means "sign in again", which the caller handles differently to a 403. */
  get isUnauthenticated() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }
}

function csrfToken(): string {
  const match = document.cookie.match(/(?:^|;\s*)pp_csrf=([^;]*)/);
  return match?.[1] ? decodeURIComponent(match[1]) : "";
}

const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (UNSAFE.has(method)) headers["X-CSRF-Token"] = csrfToken();

  const response = await fetch(`/api/v1${path}`, {
    method,
    headers,
    // Same-origin by design. The cookie is httpOnly, so this is the only way it
    // travels — there is no token in JavaScript to be stolen by an XSS.
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload = text ? safeJson(text) : null;

  if (!response.ok) {
    // FastAPI puts the message in `detail`; a validation error puts a list there.
    const detail = payload?.detail;
    const message =
      typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map((d: { msg?: string }) => d.msg ?? "Invalid value").join("; ")
          : `Request failed (${response.status})`;
    throw new ApiError(response.status, message);
  }
  return payload as T;
}

function safeJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text.slice(0, 300) };
  }
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
